import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DismantlingType, Node, Part, PartSide, partSideList, partSideNames } from '../../../../../../../../types';
import { Button, Checkbox, Confirmation, DismanltingTypeSelect, InputNumeric, Selector } from '../../../../../../components';
import { Divider, Title } from '../../../../style';
import { Bottom, Checkboxes, Container, SubParts, PartSideSwitch, CheckboxesWheel } from './style';
import amortizationStrutImage from './images/amortization-strut.png';
import clutchImage from './images/clutch.png';
import doorsImage from './images/doors.png';
import engineImage from './images/engine.png';
import luggageDoorImage from './images/luggage-door.png';
import pedalsImage from './images/pedals.png';
import steeringKnuckleImage from './images/steering-knuckle.png';
import steeringRackImage from './images/steering-rack.png';
import wheelsImage from './images/wheels.png';
import { allSidesEqual, checkOneWholePart, getAllPartsValue, getCommonPartValue, removeOneWholePart } from './helpers';

const images = {
  'engine': engineImage,
  'luggage-door': luggageDoorImage,
  'doors': doorsImage,
  'wheels': wheelsImage,
  'clutch': clutchImage,
  'pedals': pedalsImage,
  'steering-rack': steeringRackImage,
  'amortization-strut': amortizationStrutImage,
  'steering-knuckle': steeringKnuckleImage,
};

interface Props {
  node: Node;
  parts: Part[];
  title: string;
  updateNode: (value: Partial<Node>) => void;
}

export const PartCard = ({ node, parts, title, updateNode }: Props) => {
  const { t } = useTranslation();

  const [partSide, setPartSide] = useState<PartSide>('ALL');
  const [showOneWholePartConfirmation, setShowOneWholePartConfirmation] = useState(false);

  const sideIndex = useMemo(() => partSideList.indexOf(partSide), [partSide]);

  const setAll = useCallback((value: DismantlingType) => {
    if (node.type === '4-sides') {
      updateNode({
        parts4Side: Object.fromEntries(parts.map(({ name }) => [name, sideIndex === 4 ? [value, value, value, value] : [
          ...(node?.parts4Side?.[name] || []).slice(0, sideIndex),
          value,
          ...(node?.parts4Side?.[name] || []).slice(sideIndex + 1),
        ] as [DismantlingType, DismantlingType, DismantlingType, DismantlingType]])),
      });
    } else {
      updateNode({
        partsSingle: Object.fromEntries(parts.map(({ name }) => [name, value])),
      });
    }
  }, [node, sideIndex, parts, updateNode]);

  return <>
    {showOneWholePartConfirmation && <Confirmation
      title={t('Only one part selected to be included in the component')}
      text={`${t('Component should include at least 2 parts. This part also will be sold apart')}`}
      buttons={[{
        label: t('Confirm'),
        onClick: () => {
          updateNode({ ...removeOneWholePart(node), done: true });
          setShowOneWholePartConfirmation(false);
        },
      }, {
        label: t('Continue editing'),
        secondary: true,
        onClick: () => setShowOneWholePartConfirmation(false),
      }]}
      onClose={() => setShowOneWholePartConfirmation(false)}
    />}
    <Container image={images[node.name]} disabled={node.done}>
      {!node.done && <a id="undone" />}
      <div />
      <div>
        <Title size='medium' underline width='100%' marginBottom={10}>{title}</Title>
        {<SubParts>
          {node.type === '4-sides' && <PartSideSwitch>
            <span>{t(partSideNames[partSide])}</span>
            <Selector
              type={node.name === 'doors' ? 'door-side' : 'wheel-side'}
              selected={partSide}
              disabled={node.done}
              setSelected={(value) => setPartSide(value as PartSide)}
            />
          </PartSideSwitch>}
          {node.name !== 'wheels' && <Checkboxes height={parts.length === 8 ? 124 : 160} oneColumn={parts.length > 5}>
            {parts.map(({ name, title }, i) => <DismanltingTypeSelect
              key={i}
              disabled={node.done}
              value={(node.type === 'single' ? node?.partsSingle?.[name] : (sideIndex < 4 ? node?.parts4Side?.[name]?.[sideIndex] : getCommonPartValue(node, name))) || undefined}
              label={title}
              onChange={node.type === 'single' ? (value: DismantlingType) => updateNode({
                partsSingle: {
                  ...(node.partsSingle || {}),
                  [name]: value,
                },
              }) : (value: DismantlingType) => updateNode({
                parts4Side: {
                  ...(node.parts4Side || {}),
                  [name]: sideIndex < 4 ?
                    [...(node?.parts4Side?.[name] || []).slice(0, sideIndex), value, ...(node?.parts4Side?.[name] || []).slice(sideIndex + 1)] as [DismantlingType, DismantlingType, DismantlingType, DismantlingType]:
                    [value, value, value, value],
                },
              })}
            />)}
          </Checkboxes>}
          {node.name === 'wheels' && <CheckboxesWheel>
            {parts.map(({ name, title }, i) => <div key={i}>
              <InputNumeric
                key={i}
                disabled={node.done}
                maxValue={4}
                value={node?.partsWheels?.[name]?.count}
                onChange={(value: number) => updateNode({
                  partsWheels: {
                    ...(node.partsWheels || {}),
                    [name]: {
                      ...node.partsWheels?.[name],
                      count: value,
                    },
                  },
                })}
              />
              <DismanltingTypeSelect
                disabled={node.done}
                value={node?.partsWheels?.[name]?.dismantlingType}
                label={title}
                onChange={(value: DismantlingType) => updateNode({
                  partsWheels: {
                    ...(node.partsWheels || {}),
                    [name]: {
                      ...node.partsWheels?.[name],
                      dismantlingType: value,
                    },
                  },
                })}
              />
              <Checkbox
                disabled={node.done}
                value={node?.partsWheels?.[name]?.complect || false}
                label={t('Sell as a set')}
                noPadding
                small
                onChange={(value: boolean) => updateNode({
                  partsWheels: {
                    ...(node.partsWheels || {}),
                    [name]: {
                      ...node.partsWheels?.[name],
                      complect: value,
                    },
                  },
                })}
              />
            </div>)}
          </CheckboxesWheel>}
          {<>
            <Divider marginVertical={16} light />
            <Checkboxes height={28} itemWidth={162}>
              {node.name !== 'wheels' && <>
                <DismanltingTypeSelect
                  value={getAllPartsValue(node, sideIndex)}
                  label={t('Select all')}
                  disabled={node.done}
                  onChange={(value: DismantlingType) => setAll(value)}
                />
                {(node.type === '4-sides' && sideIndex < 4) && <Checkbox
                  value={allSidesEqual(node)}
                  label={t('Apply for all sides')}
                  disabled={node.done || allSidesEqual(node)}
                  noPadding
                  small
                  onChange={(value) => {
                    updateNode({
                      parts4Side: Object.fromEntries(parts.map(({ name }) => [name, [
                        node.parts4Side?.[name]?.[sideIndex],
                        node.parts4Side?.[name]?.[sideIndex],
                        node.parts4Side?.[name]?.[sideIndex],
                        node.parts4Side?.[name]?.[sideIndex],
                      ]])),
                    });

                    setPartSide('ALL');
                  }}
                />}
              </>}
            </Checkboxes>
          </>}
          <Bottom>
            {node.done && <Title size='medium'>{t('PROCESSED')}</Title>}
            <Button
              width='178px'
              margin='auto 0 0 auto'
              onClick={() => {
                if (checkOneWholePart(node)) {
                  setShowOneWholePartConfirmation(true);
                  return;
                }

                updateNode({ done: !node.done });

                if (!node.done) {
                  setTimeout(() => {
                    const element = document.querySelector('#undone');

                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                    } else {
                      const element = document.querySelector('#accept');
                      element?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' });
                    }
                  }, 0);
                }
              }}
            >{node.done ? 'Edit' : 'Done'}</Button>
          </Bottom>
        </SubParts>}
      </div>
    </Container>
  </>;
};
